import React, { useState, useEffect } from 'react'
import { KeyboardArrowDownRounded } from '@material-ui/icons/'
import FormControl from '../form-control'
import InputLabel from '../input-label'
import Tag from '../tag'
import { MuAutoComplete, TextField, Hint, Popper } from './styles'
import { composeId, convertToKebabCase } from '../../utils'

type Props = {
  'data-qa'?: string
  disableClearable?: boolean
  disabled?: boolean
  getOptionLabel: (...params: any) => any
  hint?: string
  inputValue?: string
  label: string
  multiple?: boolean
  name?: string
  onBlur?: Function
  onChange?: Function
  onInputChange?: Function
  onSelect?: any
  options: any[]
  placeholder?: string
  popupIcon?: Node
  required?: boolean
  style?: object
  theme?: 'enabled' | 'negative'
  value: any | null
  valueCallBack?: Function
}

const AutoComplete: React.FC<Props> = (props: Props) => {
  const [nextValue, setNextValue] = useState(props.value)
  const [inputValue, setInputValue] = useState(props.inputValue)
  const id = composeId(props.label)
  const dataQa = `autocomplete-${convertToKebabCase(props.label)}`
  const showHint = shouldShowHint(
    Boolean(props.hint),
    Boolean(props.value?.length),
    props.disabled,
    props.multiple,
  )

  useEffect(() => {
    props.onInputChange && props.onInputChange(inputValue)
  }, [inputValue])

  useEffect(() => {
    setNextValue(props.value)
  }, [props.value])

  function handleOnBlur() {
    if (nextValue?.length === 0) setNextValue(null)
    if (!!props.onBlur) props.onBlur(nextValue)
  }

  function handleFocusOnMultiple() {
    // quando 'multiple' está ativado, só é possível digitar se 'value' for array
    if (!!props.multiple && !nextValue) setNextValue([])
  }

  function handleChange(value: unknown) {
    props.onChange && props.onChange(value)
    props.valueCallBack && props.valueCallBack(value)
    setNextValue(value)
  }

  return (
    <FormControl>
      <InputLabel htmlFor={id} text={props.label} />

      <MuAutoComplete
        disableClearable={props.disableClearable}
        disabled={props.disabled}
        filterSelectedOptions
        getOptionLabel={props.getOptionLabel}
        id={id}
        multiple={props.multiple}
        onBlur={handleOnBlur}
        onInputChange={(_event, value) => setInputValue(value)}
        inputValue={inputValue ?? ''}
        value={nextValue || (props.multiple ? [] : null)}
        onChange={(_event, value) => handleChange(value)}
        onSelect={props.onSelect}
        options={props.options}
        PopperComponent={Popper}
        onFocus={handleFocusOnMultiple}
        popupIcon={
          !props.disabled ? (
            <KeyboardArrowDownRounded data-qa={`${dataQa}-popup`} />
          ) : null
        }
        renderInput={(params) => (
          <TextField
            {...params}
            name={props.name}
            className={`is-${props.theme}`}
            placeholder={!props.value ? props.placeholder : ''}
            variant="outlined"
            data-qa={props['data-qa'] || `autoComplete-${props.theme}`}
            style={props.style}
          />
        )}
        renderTags={(tagValue, getTagProps) => {
          return tagValue.map((option: any, index) => {
            const tagProps: any = getTagProps({ index })

            return (
              <Tag
                key={index}
                label={props.getOptionLabel(option)}
                theme={tagProps.disabled ? 'disabled' : 'active'}
                state={tagProps.disabled ? 'static' : 'removable'}
                size="small"
                onRemove={tagProps.onDelete}
              />
            )
          })
        }}
      />

      {showHint && (
        <Hint className={`is-${props.theme}`}>
          {props.hint || (props.multiple && HINT_MULTIPLE)}
        </Hint>
      )}
    </FormControl>
  )
}

AutoComplete.defaultProps = {
  disabled: false,
  multiple: false,
  required: false,
  theme: 'enabled',
  disableClearable: true,
}

export default AutoComplete

const HINT_MULTIPLE: string = 'Selecione uma ou mais opções'

function shouldShowHint(
  hasHint: boolean,
  hasValue: boolean,
  isDisabled: boolean,
  isMultiple: boolean,
): boolean {
  return hasHint || (isMultiple && !hasValue && !isDisabled)
}
